<!--
  PayrollSummaryGrid.vue
  
  Purpose: Reusable summary grid component for displaying key-value pairs
  
  Responsibilities:
  - Display summary items in a responsive grid layout
  - Support highlighting for important items (e.g., total amounts)
  - Support icons for items (optional)
  - Provide consistent styling across all summary displays
  - Handle responsive layout (stacks on mobile)
  
  Used by:
  - PayrollConciliationStatusCard (conciliation status summaries)
  - Various step components that need summary displays
  
  Props:
  - items: Array - Array of summary items
    Each item: {
      label: string - Label for the item
      value: string|number - Value to display
      highlight?: boolean - Whether to highlight this item
      icon?: string - Optional icon class (e.g., 'pi pi-check')
    }
  
  Benefits:
  - Consistent summary display across all steps
  - Responsive design (grid on desktop, stacked on mobile)
  - Easy to highlight important values
  - Supports icons for visual enhancement
  
  Data Flow:
  - Receives items array as prop
  - Renders each item in grid layout
  - Applies highlight class if item.highlight is true
  - Displays icon if item.icon is provided
-->
<template>
  <div class="summary-grid" data-testid="payroll-shared-summary-grid">
    <div
      v-for="(item, index) in items"
      :key="index"
      class="summary-item"
      :class="{ highlight: item.highlight }"
    >
      <div class="item-label">
        <i v-if="item.icon" :class="item.icon" class="item-icon"></i>
        {{ item.label }}
      </div>
      <div class="item-value">{{ item.value }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
export interface SummaryItem {
  label: string;
  value: string | number;
  highlight?: boolean;
  icon?: string;
}

interface Props {
  items: SummaryItem[];
}

defineProps<Props>();
</script>

<style scoped>
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: var(--surface-ground);
  border-radius: 6px;
  border: 1px solid var(--surface-border);
}

.summary-item.highlight {
  background: var(--primary-50);
  border-color: var(--primary-200);
  border-width: 2px;
}

.item-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color-secondary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.item-icon {
  font-size: 0.875rem;
  color: var(--primary-color);
}

.item-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
}

.summary-item.highlight .item-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
}

/* Responsive */
@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
